import { MarkerType } from "reactflow";

// Derives display label, subtitle, and icon from a camelCase rule ID.
// e.g. "ReduceVelocityLowInventory" → { label: "Reduce Velocity", sub: "Low Inventory", icon: "📉" }
function parseRuleMeta(ruleId) {
  const words = ruleId
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ");
  const label = words.slice(0, 2).join(" ");
  const sub = words.slice(2).join(" ");
  const iconMap = { Reduce: "📉", Increase: "📈", Improve: "💰" };
  const icon = iconMap[words[0]] ?? "⚙️";
  return { label, sub, icon };
}

function estimateRuleNodeHeight({
  conditions = [],
  formulas = [],
  humanReadable = [],
}) {
  const lines = (text, cpl) =>
    Math.max(1, Math.ceil((text || "").length / cpl)) * 14;

  let h = 95;

  h += 22;
  if (conditions.length > 0) {
    conditions.forEach((c) => {
      h += lines(c.label, 32) + 28;
    });
  } else {
    h += 16;
  }
  if (formulas.length > 0) {
    h += 26;
    formulas.forEach((f) => {
      h += lines(f, 22) + 3;
    });
  }
  if (humanReadable.length > 0) {
    h += 26;
    humanReadable.forEach((l) => {
      h += lines(l, 32) + 3;
    });
  }

  h += 14;
  return Math.ceil(h * 1.2);
}

function getPrioritizationNoteFromTrace(prioritization) {
  if (!prioritization) return "No prioritization data";
  const { resolution, active_before } = prioritization;
  if (resolution === "no_active_rules") return "No active rules";
  if (resolution === "first_active")
    return active_before?.length === 1
      ? "Only active rule"
      : "First active rule selected";
  if (resolution === "single_rule_active") return "Only active rule";
  if (resolution === "very_low_beats_low")
    return "VeryLow takes priority over Low";
  if (resolution === "profit_filtered_by_reduce_velocity")
    return "ImproveProfitCostOverrun discarded (ReduceVelocity active)";
  return resolution ?? "";
}

const stripTags = (text) => (text || "").replace(/<[^>]+>/g, "");

const textH = (text, cpl, lh) =>
  Math.max(1, Math.ceil(stripTags(text).length / cpl)) * lh;

function estimatePrioritizeNodeHeight({ note }) {
  let h = 12;
  h += 22;
  h += 30;
  if (note) {
    h += 12;
    h += textH(note, 36, 17);
  }
  h += 12;
  return Math.ceil(h * 1.2);
}

function estimateActionNodeHeight({ rawSteps }) {
  let h = 12;
  h += 22;
  rawSteps.forEach((step, i) => {
    h += 30;
    if (step.human_readable_description)
      h += textH(step.human_readable_description, 40, 15) + 8;
    if (step.calculation) h += textH(step.calculation, 38, 15) + 16;
    if (i < rawSteps.length - 1) h += 4;
  });
  h += 12;
  return Math.ceil(h * 1.2);
}

const RULE_SPACING = 340;
const RULE_NODE_WIDTH = 260;
const CENTER_NODE_WIDTH = 320;

export function buildTreeFromTrace({
  trace,
  asin,
  store,
  features = {},
  decision = {},
}) {
  if (!trace) return { nodes: [], edges: [] };

  const {
    rule_nodes = [],
    prioritization = {},
    action_steps = [],
    result = {},
  } = trace;
  const winner = prioritization.winner ?? null;
  const action = result.action || decision.action || "";
  const isDoNothing = !winner || !action || action === "Do Nothing";
  const isVeryLow = winner === "ReduceVelocityVeryLowInventory";

  const totalRulesWidth =
    (rule_nodes.length - 1) * RULE_SPACING + RULE_NODE_WIDTH;
  const centerX = totalRulesWidth / 2 - CENTER_NODE_WIDTH / 2;

  const maxRuleHeight = Math.max(
    ...rule_nodes.map((n) =>
      estimateRuleNodeHeight({
        conditions: n.conditions ?? [],
        formulas: n.formulas ?? [],
        humanReadable: n.human_readable_conditions ?? [],
      })
    ),
    100
  );
  const note = getPrioritizationNoteFromTrace(prioritization);
  const rawSteps = action_steps.length
    ? action_steps
    : [{ label: "No action steps recorded" }];

  const RULE_ROW_Y = 260;
  const prioritizeY = RULE_ROW_Y + maxRuleHeight + 40;
  const actionY = prioritizeY + estimatePrioritizeNodeHeight({ note }) + 40;
  const resultY = actionY + estimateActionNodeHeight({ rawSteps }) + 40;

  const activeStyle = { stroke: "#3b82f6", strokeWidth: 2.5 };
  const hitStyle = { stroke: "#16a34a", strokeWidth: 2.5 };
  const inactiveStyle = { stroke: "#cbd5e1", strokeWidth: 1.5 };
  const activeMarker = {
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: "#3b82f6",
      width: 16,
      height: 16,
    },
  };
  const hitMarker = {
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: "#16a34a",
      width: 16,
      height: 16,
    },
  };

  const winnerMeta = winner ? parseRuleMeta(winner) : null;

  const nodes = [
    {
      id: "header",
      type: "headerNode",
      position: { x: centerX, y: 0 },
      data: {
        asin,
        store,
        color: "#1e3a5f",
      },
    },

    ...rule_nodes.map((traceNode, i) => {
      const hit = traceNode.result === "HIT";
      const isWinner = traceNode.rule === winner;
      const { label, sub, icon } = parseRuleMeta(traceNode.rule);
      return {
        id: `rule-${i}`,
        type: "ruleNode",
        position: { x: i * RULE_SPACING, y: RULE_ROW_Y },
        data: {
          ruleId: traceNode.rule,
          label,
          sub,
          icon,
          conditions: traceNode.conditions ?? [],
          formulas: traceNode.formulas ?? [],
          humanReadable: traceNode.human_readable_conditions ?? [],
          features,
          hit,
          isWinner,
          color: isWinner ? "#15803d" : hit ? "#16a34a" : "#94a3b8",
        },
      };
    }),

    {
      id: "prioritize",
      type: "prioritizeNode",
      position: { x: centerX, y: prioritizeY },
      data: {
        winner,
        winnerLabel: winnerMeta
          ? `${winnerMeta.icon} ${winnerMeta.label} (${winnerMeta.sub})`
          : null,
        note,
        isDoNothing,
        features,
        color: isDoNothing ? "#94a3b8" : "#2563eb",
      },
    },

    {
      id: "action",
      type: "actionNode",
      position: { x: centerX, y: actionY },
      data: {
        rawSteps,
        features,
        isDoNothing,
        isVeryLow,
        actionType: action.includes("PPC")
          ? "ppc"
          : action.includes("Price")
          ? "price"
          : "nothing",
        color: isDoNothing ? "#94a3b8" : isVeryLow ? "#dc2626" : "#d97706",
      },
    },

    {
      id: "result",
      type: "resultNode",
      position: { x: centerX, y: resultY },
      data: {
        action,
        targetPrice: result.target_price ?? decision.target_price,
        targetPpc: result.target_ppc_budget ?? decision.target_ppc_budget,
        currentPrice: features.AsinSellingPrice,
        currentPpc: features.CombinedPpcCosts,
        reasoning: result.reasoning ?? null,
        reasoning2: result.reasoning2 ?? null,
        features,
        isDoNothing,
        isVeryLow,
        color: isDoNothing ? "#6b7280" : isVeryLow ? "#dc2626" : "#1e3a5f",
      },
    },
  ];

  const edges = [
    ...rule_nodes.map((_, i) => ({
      id: `e-h-r${i}`,
      source: "header",
      target: `rule-${i}`,
      type: "smoothstep",
      style: inactiveStyle,
    })),

    ...rule_nodes.map((traceNode, i) => {
      const isActive = traceNode.rule === winner;
      return {
        id: `e-r${i}-p`,
        source: `rule-${i}`,
        target: "prioritize",
        type: "smoothstep",
        animated: isActive,
        style: isActive ? hitStyle : inactiveStyle,
        ...(isActive ? hitMarker : {}),
      };
    }),

    {
      id: "e-p-a",
      source: "prioritize",
      target: "action",
      type: "smoothstep",
      animated: !isDoNothing,
      style: isDoNothing ? inactiveStyle : activeStyle,
      ...(isDoNothing ? {} : activeMarker),
    },

    {
      id: "e-a-r",
      source: "action",
      target: "result",
      type: "smoothstep",
      animated: !isDoNothing,
      style: isDoNothing ? inactiveStyle : activeStyle,
      ...(isDoNothing ? {} : activeMarker),
    },
  ];

  return { nodes, edges };
}
